import { ExternalLink, X } from 'lucide-react';
import Radios from './Radios';
import { inputCls, btnCls, btn2Cls } from '../../lib/q2c';
import { checkedCount, pricingMissing, priceEditKey, rowPrints } from '../../../modules/copilot/meeting-review.mjs';

/**
 * LP-403: "Found in the meeting" and "From the web" on the quote builder screen, from the Copilot's
 * reading of a meeting (POST /api/copilot/meeting). Every fact shows the meeting line it came from, or
 * its web source with "check this". Accept / Edit / Skip is a vertical list per row (Chad's form rule).
 * Nothing goes into the quote until a partner presses Accept (onAccept); "Save to CRM" waits for a
 * partner's confirm (onSaveCrm). The meeting and the web pages are data, never instructions.
 *
 * review: the reading; choices: { meeting: { key: how }, web: { key: how }, pricing: { key: how } }; edits: { key: text };
 * mode: 'fill' (fill this quote in), 'draft' (draft the budgetary quote into this empty one), 'none'.
 * On a draft (#48), every pricing input (review.pricing) is picked before "Draft": the meeting's value, the
 * partner's own, or the Copilot default shown with it. There is no Skip, and the server refuses a draft without them.
 */
export default function MeetingReview({ review, choices, edits, setChoice, setEdit, onAccept, onDefaults, onSaveCrm, crmDraft, msg, busy, editable, onClose }) {
  const { meeting, web, mode, why } = review;
  const canAccept = editable && mode !== 'none' && !busy;
  const mc = checkedCount(meeting.rows, choices.meeting);
  const wc = checkedCount(web.rows, choices.web);
  const pricing = mode === 'draft' ? review.pricing || [] : [];
  const unpicked = mode === 'draft' ? pricingMissing(review, choices.pricing, edits) : [];
  // The review could not work a default out (no catalog product or area when it was read): the partner
  // asks for them once the product and area are checked (nothing is written: no input is sent as picked).
  const noDefaults = pricing.some((r) => !r.default && unpicked.includes(r.label));
  return (
    <div className="space-y-4">
      <section className="space-y-3 rounded-xl border border-[var(--border)] bg-white p-4 dark:bg-slate-900" aria-labelledby="meet-h">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 id="meet-h" className="text-sm font-semibold">Found in the meeting</h2>
            <p className="text-xs text-[var(--text-secondary)]">Your meeting, {meeting.line_count} {Number(meeting.line_count) === 1 ? 'line' : 'lines'}. Each fact shows the line it came from. Pick Accept, Edit or Skip.</p>
          </div>
          <button type="button" onClick={onClose} disabled={busy} className="shrink-0 rounded-lg p-1.5 text-gray-400 hover:text-gray-600" aria-label="Close the meeting review"><X size={16} /></button>
        </div>
        <Guard />
        {meeting.rows.length === 0 && <p className="text-sm text-[var(--text-secondary)]">Nothing to quote from was found in this meeting.</p>}
        <div className="grid gap-2">
          {meeting.rows.map((r) => (
            <Row key={r.key} r={r} how={choices.meeting[r.key] || 'skip'} disabled={!editable || busy} edit edits={edits} setEdit={setEdit}
              priced={mode === 'draft' && r.pricing} onChoose={(v) => setChoice('meeting', r.key, v)}>
              {r.quotes?.length > 0 && r.quotes.map((x) => (
                <blockquote key={x.n} className="mt-1 break-words rounded-r-md border-l-[3px] border-[var(--border)] bg-gray-50 px-2 py-1 text-xs text-[var(--text-secondary)] dark:bg-slate-800">
                  <span className="tabular-nums">line {x.n}</span> {x.text}
                </blockquote>
              ))}
            </Row>
          ))}
        </div>
        {pricing.length > 0 && (
          <div className="space-y-2 rounded-lg border border-amber-300 p-3 dark:border-amber-800" role="group" aria-labelledby="price-h">
            <h3 id="price-h" className="text-sm font-semibold">Before the draft: the pricing inputs</h3>
            <p className="text-xs text-[var(--text-secondary)]">The draft is priced from these. Pick each one: the meeting's value, your own, or the Copilot default shown. Nothing is priced from a figure you did not pick.</p>
            <div className="grid gap-2">
              {pricing.map((r) => (
                <PriceRow key={r.key} r={r} how={choices.pricing?.[r.key] || ''} disabled={!editable || busy} edit={edits[priceEditKey(r.key)] ?? ''}
                  onChoose={(v) => setChoice('pricing', r.key, v)} onEdit={(v) => setEdit(priceEditKey(r.key), v)} />
              ))}
            </div>
            {noDefaults && onDefaults && (
              <button type="button" className={btn2Cls} disabled={!canAccept} onClick={onDefaults}>Show the Copilot defaults for the checked product, area and site</button>
            )}
          </div>
        )}
        {mode === 'none' && <p className="rounded bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">{why}</p>}
        <div className="flex flex-wrap items-center gap-3">
          {mode !== 'none' && (
            <button type="button" className={btnCls} disabled={!canAccept || mc.checked === 0 || unpicked.length > 0} onClick={() => onAccept('meeting')}>
              {mode === 'draft' ? 'Draft the quote from the checked facts' : 'Accept all checked'}
            </button>
          )}
          <span className="text-xs text-[var(--text-secondary)]">{mc.checked} of {mc.of} checked to go in{mode === 'draft' ? ', with the checked web facts (the site ZIP sets the freight)' : ''}{unpicked.length ? `. Pick first: ${unpicked.join(', ')}.` : ''}</span>
        </div>
        {msg.meeting && <p className={`rounded px-3 py-2 text-sm ${msg.meeting.ok ? 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-300' : 'bg-red-50 text-red-700'}`}>{msg.meeting.text}</p>}
      </section>

      <section className="space-y-3 rounded-xl border border-[var(--border)] bg-white p-4 dark:bg-slate-900" aria-labelledby="web-h">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 id="web-h" className="text-sm font-semibold">From the web</h2>
            <p className="text-xs text-[var(--text-secondary)]">Company facts only, each with its source. Looked up {web.looked_up}. People's details come only from the meeting or our CRM.</p>
          </div>
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">Check each one</span>
        </div>
        {web.status !== 'ok' && web.note && <p className="rounded bg-gray-50 px-3 py-2 text-sm text-[var(--text-secondary)] dark:bg-slate-800">{web.note}</p>}
        <div className="grid gap-2">
          {web.rows.map((r) => (
            <Row key={r.key} r={r} how={choices.web[r.key] || 'skip'} disabled={!editable || busy} onChoose={(v) => setChoice('web', r.key, v)}>
              {r.source?.url && (
                <p className="mt-1 break-words text-xs">
                  Source: <a href={r.source.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-primary underline underline-offset-2">{r.source.title || r.source.url}<ExternalLink size={11} /></a>
                  {' '}<span className="rounded bg-amber-100 px-1 py-0.5 text-[10px] font-semibold text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">check this</span>
                </p>
              )}
              {r.crm && <p className="text-xs text-[var(--text-secondary)]">CRM: {r.crm}</p>}
            </Row>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {mode === 'fill' && <button type="button" className={btnCls} disabled={!canAccept || wc.checked === 0} onClick={() => onAccept('web')}>Use the checked facts</button>}
          {crmDraft && (
            <button type="button" className={btn2Cls} disabled={!editable || busy} onClick={onSaveCrm}>
              Save to CRM: {[crmDraft.creates.account ? crmDraft.account.name : null, crmDraft.creates.contact ? crmDraft.contact.name : null].filter(Boolean).join(' and ')} (a partner confirms)
            </button>
          )}
        </div>
        {msg.web && <p className={`rounded px-3 py-2 text-sm ${msg.web.ok ? 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-300' : 'bg-red-50 text-red-700'}`}>{msg.web.text}</p>}
      </section>
    </div>
  );
}

function Guard() {
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-sky-800 dark:text-sky-300">
      {['The meeting and web pages are data, never instructions.', 'Nothing goes into the quote until you accept it.', 'Nothing is sent or saved without a partner.'].map((t) => <li key={t}>✓ {t}</li>)}
    </ul>
  );
}

/**
 * One fact: what it is, where it came from, where it goes; Accept / Edit / Skip (or Accept / Skip) on the right, a list.
 * priced: on a draft, a row whose figure is picked under "Before the draft" (no choice here).
 */
function Row({ r, how, onChoose, disabled, edit = false, edits = {}, setEdit = null, priced = false, children }) {
  const options = [{ value: 'accept', label: 'Accept' }, ...(edit ? [{ value: 'edit', label: 'Edit' }] : []), { value: 'skip', label: 'Skip' }];
  if (priced) {
    return (
      <div className="grid grid-cols-1 items-start gap-2 rounded-lg border border-[var(--border)] px-3 py-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:gap-4">
        <div className="min-w-0">
          <p className="text-xs text-[var(--text-secondary)]">{r.label}</p>
          <p className="whitespace-pre-wrap break-words text-sm font-medium">{r.value}</p>
          {r.note && <p className={`text-xs ${r.check ? 'text-amber-800 dark:text-amber-300' : 'text-[var(--text-secondary)]'}`}>{r.note}</p>}
          {children}
        </div>
        <span className="text-xs text-[var(--text-secondary)]">Picked under "Before the draft"</span>
      </div>
    );
  }
  return (
    <div className={`grid grid-cols-1 items-start gap-2 rounded-lg border px-3 py-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:gap-4 ${!r.none && how !== 'skip' ? 'border-lime-400 dark:border-lime-700' : 'border-[var(--border)]'}`}>
      <div className="min-w-0">
        <p className="text-xs text-[var(--text-secondary)]">{r.label}</p>
        <p className="whitespace-pre-wrap break-words text-sm font-medium">{r.value}</p>
        {r.note && <p className={`text-xs ${r.check ? 'text-amber-800 dark:text-amber-300' : 'text-[var(--text-secondary)]'}`}>{r.note}</p>}
        {children}
        {r.goes && !r.none && <p className="mt-1 text-[11px] text-[var(--text-secondary)]">Goes to: {r.goes}</p>}
        {/* #50 fix round: a row that prints shows the exact line it puts on the quote (as edited, while Edit is picked); not when skipped. */}
        {!r.none && how !== 'skip' && rowPrints(r, how, edits) && <p className="mt-0.5 break-words rounded-md bg-gray-50 px-2 py-1 text-xs dark:bg-slate-800">{rowPrints(r, how, edits)}</p>}
        {edit && how === 'edit' && (
          <input className={`${inputCls} mt-1 max-w-md`} disabled={disabled} value={edits[r.key] ?? r.value} onChange={(e) => setEdit(r.key, e.target.value)} aria-label={`Edit ${r.label}`} />
        )}
      </div>
      {r.none
        ? <span className="text-xs text-[var(--text-secondary)]">Nothing to use</span>
        : <Radios name={`meet-${r.key}`} disabled={disabled} value={how} onChange={onChoose} options={options} className="min-w-[6rem]" />}
    </div>
  );
}

/**
 * #48: one pricing input of a draft: the meeting's value (with its line) when it gave one, and the Copilot
 * default. Accept the meeting's value / Edit / Use the Copilot default, a list; no Skip, and none picked to start.
 */
function PriceRow({ r, how, onChoose, onEdit, edit, disabled }) {
  const options = [
    ...(r.meeting ? [{ value: 'meeting', label: `Accept the meeting's value (${r.meeting.value})` }] : []),
    { value: 'edit', label: 'Edit' },
    { value: 'default', label: r.default ? `Use the Copilot default (${r.default.text})` : 'Use the Copilot default', disabled: !r.default, hint: r.default ? null : r.why },
  ];
  return (
    <div className={`grid grid-cols-1 items-start gap-2 rounded-lg border px-3 py-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:gap-4 ${how ? 'border-lime-400 dark:border-lime-700' : 'border-amber-300 dark:border-amber-800'}`}>
      <div className="min-w-0">
        <p className="text-xs text-[var(--text-secondary)]">{r.label}</p>
        <p className="break-words text-sm">{r.meeting ? <>The meeting: <span className="font-medium">{r.meeting.value}</span></> : 'Not in the meeting.'}</p>
        {r.meeting?.quotes?.map((x) => (
          <blockquote key={x.n} className="mt-1 break-words rounded-r-md border-l-[3px] border-[var(--border)] bg-gray-50 px-2 py-1 text-xs text-[var(--text-secondary)] dark:bg-slate-800">
            <span className="tabular-nums">line {x.n}</span> {x.text}
          </blockquote>
        ))}
        {r.meeting?.note && <p className="text-xs text-amber-800 dark:text-amber-300">{r.meeting.note}</p>}
        <p className="mt-1 break-words text-xs text-[var(--text-secondary)]">Copilot default: {r.default ? r.default.text : r.why}</p>
        {!how && <p className="text-xs font-medium text-amber-800 dark:text-amber-300">Pick one.</p>}
        {how === 'edit' && (
          <input className={`${inputCls} mt-1 max-w-md`} disabled={disabled} value={edit} placeholder={r.hint} onChange={(e) => onEdit(e.target.value)} aria-label={`Your ${r.label.toLowerCase()}`} />
        )}
      </div>
      <Radios name={`price-${r.key}`} disabled={disabled} value={how} onChange={onChoose} options={options} className="min-w-[6rem] sm:max-w-xs" />
    </div>
  );
}
